import { useDispatch } from 'react-redux'
import { Device, Project, RuuviTag, Valve } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import {
  buildAlertStructure,
  isDeviceAlertGroup,
  isDeviceAlertRule,
  isDeviceAlertTemplate,
  isProjectAlertGroup,
  isProjectAlertRule,
  isProjectAlertTemplate,
  isRuuviTagAlertGroup,
  isRuuviTagAlertRule,
  isRuuviTagAlertTemplate,
  isValveAlertGroup,
  isValveAlertRule,
  isValveAlertTemplate
} from '../utils/alert'
import { isValve } from '../utils/device'
import useEnabledAlertRules, { EnabledAlertRules } from './useEnabledAlertRules'
import { getProjectAlertRules, updateProjectAlertRules } from '../services/projectService'
import { getRuuviTagAlertRules, updateRuuviTagAlertRules } from '../services/ruuviTagService'
import { setProjectAlertRules } from '../redux/slices/projects'
import { setAlertRules as setRuuviTagAlertRules } from '../redux/slices/ruuviTag'
import { setAlertRules as setDeviceAlertRules } from '../redux/slices/devices'
import { setAlertRules as setValveAlertRules } from '../redux/slices/valve'
import { getDeviceAlertRules, updateDeviceAlertRules } from '../services/deviceService'
import { getValveAlertRules, updateValveAlertRules } from '../services/valve'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

type AlertRules = {
  projectAlertTemplates: AlertTemplate[]
  projectAlertRules: AlertGroup[]
  deviceAlertTemplates: AlertTemplate[]
  deviceAlertRules: AlertGroup[]
  valveAlertTemplates: AlertTemplate[]
  valveAlertRules: AlertGroup[]
  ruuviTagAlertTemplates: AlertTemplate[]
  ruuviTagAlertRules: AlertGroup[]
  updateAlertRules: (input: AlertRuleDataGridRow[]) => Promise<AlertGroup[]>
} & EnabledAlertRules

interface UseAlertRulesProps {
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  alertTemplates: AlertTemplate[]
  alertRules: AlertGroup[]
  useRedux?: boolean
}

export default function useAlertRules({
  project,
  device,
  ruuviTag,
  alertTemplates,
  alertRules,
  useRedux = true
}: UseAlertRulesProps): AlertRules {
  const dispatch = useDispatch()

  const {
    enabledAlertRules,
    enabledProjectAlertRules,
    enabledDeviceAlertRules,
    eanbledValveAlertRules,
    enabledRuuviTagAlertRules
  } = useEnabledAlertRules(alertRules)

  /**
   * The project alert templates.
   */
  const projectAlertTemplates = alertTemplates.filter(isProjectAlertTemplate)

  /**
   * The project alert rules.
   */
  const projectAlertRules = alertRules.filter(isProjectAlertGroup)

  /**
   * The device alert templates.
   */
  const deviceAlertTemplates = alertTemplates.filter(isDeviceAlertTemplate)

  /**
   * The device alert rules.
   */
  const deviceAlertRules = alertRules.filter(isDeviceAlertGroup)

  /**
   * The valve alert templates.
   */
  const valveAlertTemplates = alertTemplates.filter(isValveAlertTemplate)

  /**
   * The valve alert rules.
   */
  const valveAlertRules = alertRules.filter(isValveAlertGroup)

  /**
   * The ruuvitag alert templates.
   */
  const ruuviTagAlertTemplates = alertTemplates.filter(isRuuviTagAlertTemplate)

  /**
   * The ruuvitag alert rules.
   */
  const ruuviTagAlertRules = alertRules.filter(isRuuviTagAlertGroup)

  /**
   * Update alert rules.
   */
  async function updateAlertRules(input: AlertRuleDataGridRow[]): Promise<AlertGroup[]> {
    let alertGroups: AlertGroup[] = []

    if (project) {
      const newProjectAlertRules = enabledProjectAlertRules.concat(input.filter(isProjectAlertRule))

      alertGroups = alertGroups.concat(
        await handleUpdateProjectAlertRules(
          project,
          buildAlertStructure(newProjectAlertRules, projectAlertTemplates)
        )
      )
    }

    if (device) {
      const newDeviceAlertRules = enabledDeviceAlertRules.concat(input.filter(isDeviceAlertRule))

      alertGroups = alertGroups.concat(
        await handleUpdateDeviceAlertRules(
          device,
          buildAlertStructure(newDeviceAlertRules, deviceAlertTemplates)
        )
      )

      if (isValve(device)) {
        const newValveAlertRules = eanbledValveAlertRules.concat(input.filter(isValveAlertRule))

        alertGroups = alertGroups.concat(
          await handleUpdateValveAlertRules(
            device,
            buildAlertStructure(newValveAlertRules, valveAlertTemplates)
          )
        )
      }
    } else if (ruuviTag) {
      const newRuuviTagAlertRules = enabledRuuviTagAlertRules.concat(
        input.filter(isRuuviTagAlertRule)
      )

      alertGroups = alertGroups.concat(
        await handleUpdateRuuviTagAlertRules(
          ruuviTag,
          buildAlertStructure(newRuuviTagAlertRules, ruuviTagAlertTemplates)
        )
      )
    }

    return alertGroups
  }

  /**
   * Update alert rules of a project.
   */
  async function handleUpdateProjectAlertRules(
    project: Project,
    input: AlertGroup[]
  ): Promise<AlertGroup[]> {
    await updateProjectAlertRules(project.id, input.filter(isProjectAlertGroup))
    const updatedProjectAlertRules = await getProjectAlertRules(project.id)

    if (useRedux) {
      dispatch(setProjectAlertRules(updatedProjectAlertRules))
    }

    return updatedProjectAlertRules
  }

  /**
   * Update alert rules of a ruuvitag.
   */
  async function handleUpdateRuuviTagAlertRules(
    ruuviTag: RuuviTag,
    input: AlertGroup[]
  ): Promise<AlertGroup[]> {
    await updateRuuviTagAlertRules(ruuviTag.id, input.filter(isRuuviTagAlertGroup))
    const updatedRuuviTagAlertRules = await getRuuviTagAlertRules(ruuviTag.id)

    if (useRedux) {
      dispatch(setRuuviTagAlertRules(updatedRuuviTagAlertRules))
    }

    return updatedRuuviTagAlertRules
  }

  /**
   * Update alert rules of a device.
   */
  async function handleUpdateDeviceAlertRules(
    device: Device,
    input: AlertGroup[]
  ): Promise<AlertGroup[]> {
    await updateDeviceAlertRules(device.name, input.filter(isDeviceAlertGroup))
    const updatedDeviceAlertRules = await getDeviceAlertRules(device.name)

    if (useRedux) {
      dispatch(setDeviceAlertRules(updatedDeviceAlertRules))
    }

    return updatedDeviceAlertRules
  }

  /**
   * Update alert rules of a valve.
   */
  async function handleUpdateValveAlertRules(
    valve: Valve,
    input: AlertGroup[]
  ): Promise<AlertGroup[]> {
    await updateValveAlertRules(valve.name, input.filter(isValveAlertGroup))
    const updatedValveAlertRules = await getValveAlertRules(valve.name)

    if (useRedux) {
      dispatch(setValveAlertRules(updatedValveAlertRules))
    }

    return updatedValveAlertRules
  }

  return {
    projectAlertTemplates,
    projectAlertRules,
    deviceAlertTemplates,
    deviceAlertRules,
    valveAlertTemplates,
    valveAlertRules,
    ruuviTagAlertTemplates,
    ruuviTagAlertRules,
    enabledAlertRules,
    enabledProjectAlertRules,
    enabledDeviceAlertRules,
    eanbledValveAlertRules,
    enabledRuuviTagAlertRules,
    updateAlertRules
  }
}
